import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_BASE;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const login = async (identifier, password) => {
  // Step 1 of the (optional) two-step gate. Returns either:
  //   { access_token, user }                         — login complete
  //   { requires_access_code: true, challenge, positions, ... } — step 2 needed
  // Credentials are sent in the request body (not the URL) to avoid leaking
  // them into server/proxy logs and browser history.
  const response = await api.post('/auth/login-unified', { identifier, password });
  return response.data;
};

export const forgotPassword = async (email) => {
  const response = await api.post('/auth/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token, new_password) => {
  const response = await api.post('/auth/reset-password', { token, new_password });
  return response.data;
};

export const verifyAccessCode = async (challenge, code) => {
  // Step 2 — submit the 5-digit partial-PIN against the challenge.
  // Returns { access_token, user } on success.
  const response = await api.post('/auth/login-verify', { challenge, code });
  return response.data;
};

export const logout = () => {
  localStorage.removeItem('admin_token');
};

// Interview Management APIs - FIXED: Now using /interview/* prefix
export const getCandidates = async () => {
  const response = await api.get('/interview/candidates');
  return response.data;
};

export const getRequirements = async () => {
  const response = await api.get('/interview/requirements');
  return response.data;
};

export const getOrganisations = async () => {
  const response = await api.get('/interview/organisations');
  return response.data;
};

export const generateQuestions = async (candidateId, requirementId, organisationId, enabledPhases, totalQaQuestions = 9, customQuestions = [], difficultyLevel = 5) => {
  const response = await api.post('/interview/generate-questions', {
    candidate_id: candidateId,
    requirement_id: requirementId,
    organisation_id: organisationId,
    enabled_phases: enabledPhases,
    total_qa_questions: totalQaQuestions,
    custom_questions: customQuestions.map(q => ({
      question: q.question,
      expected_keywords: q.expectedKeywords || ''
    })),
    difficulty_level: difficultyLevel
  });
  return response.data;
};

export const checkQuestions = async (candidateId, requirementId, organisationId) => {
  const response = await api.get(`/interview/check-questions/${candidateId}`, {
    params: { requirement_id: requirementId, organisation_id: organisationId }
  });
  return response.data;
};

export const getGeneratedQuestions = async (candidateId, requirementId, organisationId) => {
  const response = await api.get(`/interview/preview-questions/${candidateId}`, {
    params: { requirement_id: requirementId, organisation_id: organisationId }
  });
  return response.data;
};

export const generateToken = async (
  candidateId, requirementId, organisationId,
  { assignedProctorId = null, interviewType = 'ai', panelInterviewerIds = [] } = {}
) => {
  const response = await api.post('/interview/generate-token', {
    candidate_id: candidateId,
    requirement_id: requirementId,
    organisation_id: organisationId,
    assigned_proctor_id: assignedProctorId || null,
    interview_type: interviewType || 'ai',
    panel_interviewer_ids: panelInterviewerIds || []
  });
  return response.data;
};

export const sendInterview = async (
  candidateId, requirementId, organisationId,
  { assignedProctorId = null, interviewType = 'ai', panelInterviewerIds = [] } = {}
) => {
  const response = await api.post('/interview/send-interview', {
    candidate_id: candidateId,
    requirement_id: requirementId,
    organisation_id: organisationId,
    assigned_proctor_id: assignedProctorId || null,
    interview_type: interviewType || 'ai',
    panel_interviewer_ids: panelInterviewerIds || []
  });
  return response.data;
};

export const getSentInterviews = async () => {
  const response = await api.get('/interview/sent-interviews');
  return response.data;
};

// Proctors (human interviewers). Org-scoped for non-super-admins.
export const getProctors = async () => {
  const response = await api.get('/users/proctors');
  return response.data;
};

export const createProctor = async (proctor) => {
  const response = await api.post('/users/proctors', proctor);
  return response.data;
};

// Interviewers / panelists (conduct human-led panel interviews with the copilot).
export const getInterviewers = async () => {
  const response = await api.get('/users/interviewers');
  return response.data;
};

export const createInterviewer = async (interviewer) => {
  const response = await api.post('/users/interviewers', interviewer);
  return response.data;
};

// ── External integrations (pull requirements/candidates from a configured source) ──
export const listIntegrations = async () => {
  const response = await api.get('/integrations');
  return response.data;
};
export const createIntegration = async (conn) => {
  const response = await api.post('/integrations', conn);
  return response.data;
};
export const deleteIntegration = async (id) => {
  const response = await api.delete(`/integrations/${id}`);
  return response.data;
};
export const testIntegration = async (id) => {
  const response = await api.post(`/integrations/${id}/test`);
  return response.data;
};
export const syncIntegration = async (id) => {
  const response = await api.post(`/integrations/${id}/sync`);
  return response.data;
};
// Discover an MCP server's tools (before saving the connection).
export const discoverMcp = async (serverUrl, auth = {}) => {
  const response = await api.post('/integrations/mcp/discover', { server_url: serverUrl, auth });
  return response.data;
};
export const previewIntegration = async (id, entity = 'requirements', limit = 25) => {
  const response = await api.post(`/integrations/${id}/preview`, { entity, limit });
  return response.data;
};
export const importFromIntegration = async (id, entity, items) => {
  const response = await api.post(`/integrations/${id}/import`, { entity, items });
  return response.data;
};
export const importCsv = async (entity, items) => {
  const response = await api.post('/integrations/csv/import', { entity, items });
  return response.data;
};
// Conversational copilot: agent fetches from configured sources and proposes records.
export const integrationCopilot = async (messages, entity = 'requirements') => {
  const response = await api.post('/integrations/copilot', { messages, entity });
  return response.data;
};

// ── Platform users (super_admin only) ────────────────────────────────────────
export const getAllUsers = async () => (await api.get('/users/list')).data;

// ── Business Units + P&L (internal platform divisions) ─────────────────────
export const listBusinessUnits = async () => (await api.get('/business-units')).data;
export const getBusinessUnit  = async (id) => (await api.get(`/business-units/${id}`)).data;
export const createBusinessUnit = async (body) => (await api.post('/business-units', body)).data;
export const updateBusinessUnit = async (id, patch) => (await api.patch(`/business-units/${id}`, patch)).data;
export const deleteBusinessUnit = async (id) => (await api.delete(`/business-units/${id}`)).data;
export const addBuMember = async (id, user_id) => (await api.post(`/business-units/${id}/members`, { user_id })).data;
export const removeBuMember = async (id, user_id) => (await api.delete(`/business-units/${id}/members/${user_id}`)).data;
export const addBuOrg = async (id, organization_id) => (await api.post(`/business-units/${id}/orgs`, { organization_id })).data;
export const removeBuOrg = async (id, organization_id) => (await api.delete(`/business-units/${id}/orgs/${organization_id}`)).data;
export const listBuCosts = async (id) => (await api.get(`/business-units/${id}/costs`)).data;
export const addBuCost = async (id, body) => (await api.post(`/business-units/${id}/costs`, body)).data;
export const deleteBuCost = async (id, cost_id) => (await api.delete(`/business-units/${id}/costs/${cost_id}`)).data;
export const getBuPL = async (id, period = 'all') => (await api.get(`/business-units/${id}/pl`, { params: { period } })).data;

// ── MIS dashboards (platform admin) ──────────────────────────────────────────
export const getMisOperations = async () => (await api.get('/mis/operations')).data;
export const getMisFinancial  = async () => (await api.get('/mis/financial')).data;
export const getMisSupport    = async () => (await api.get('/mis/support')).data;
export const getMisAccounting = async () => (await api.get('/mis/accounting')).data;

// ── Billing / interview credits ──────────────────────────────────────────────
const orgParams = (orgId) => (orgId ? { params: { organization_id: orgId } } : {});
export const getBillingSettings = async () => (await api.get('/billing/settings')).data;
export const updateBillingSettings = async (s) => (await api.put('/billing/settings', s)).data;
export const getCreditBalance = async (orgId) => (await api.get('/billing/balance', orgParams(orgId))).data;
export const createCreditOrder = async (credits, orgId) => (await api.post('/billing/order', { credits }, orgParams(orgId))).data;
export const verifyPayment = async (payload, orgId) => (await api.post('/billing/verify', payload, orgParams(orgId))).data;
export const grantCredits = async (organization_id, credits, note) => (await api.post('/billing/grant', { organization_id, credits, note })).data;
export const getPayments = async (orgId) => (await api.get('/billing/payments', orgParams(orgId))).data;

// Reassign (or clear) the proctor on an interview, by token or candidate.
export const assignProctor = async ({ token, candidateId, assignedProctorId }) => {
  const response = await api.post('/interview/assign-proctor', {
    token: token || null,
    candidate_id: candidateId || null,
    assigned_proctor_id: assignedProctorId || null
  });
  return response.data;
};

// Set interview type + panel of interviewers, by token or candidate.
export const assignPanel = async ({ token, candidateId, interviewType, panelInterviewerIds }) => {
  const response = await api.post('/interview/assign-panel', {
    token: token || null,
    candidate_id: candidateId || null,
    interview_type: interviewType || null,
    panel_interviewer_ids: panelInterviewerIds || []
  });
  return response.data;
};

export const generateInterviewToken = async (candidateId, requirementId, organisationId) => {
  const response = await api.post('/interview/generate-token', {
    candidate_id: candidateId,
    requirement_id: requirementId,
    organisation_id: organisationId
  });
  return response.data;
};

// Candidate CRUD APIs - FIXED: /candidate/* → /candidates/*
export const getCandidatesList = async () => {
  const response = await api.get('/candidates/get');
  return response.data;
};

export const addNewCandidate = async (candidateData) => {
  const response = await api.post('/candidates/add', candidateData);
  return response.data;
};

export const updateExistingCandidate = async (candidateId, candidateData) => {
  const response = await api.put(`/candidates/update/${candidateId}`, candidateData);
  return response.data;
};

export const getCandidateDetails = async (candidateId) => {
  const response = await api.get(`/candidates/findone/${candidateId}`);
  return response.data;
};

export const deleteCandidateById = async (candidateId) => {
  const response = await api.delete(`/candidates/delete/${candidateId}`);
  return response.data;
};

// Candidate self-service API
export const getMyCandidate = async () => {
  const response = await api.get('/candidates/me');
  return response.data;
};

// ─────────────────────────── Campaigns ───────────────────────────
// One JD → many candidates: shared question set generated from the JD, a
// per-CV greeting per candidate, and bulk invitations on launch.
export const getCampaigns = async () =>
  (await api.get('/campaigns')).data;

export const getCampaign = async (id) =>
  (await api.get(`/campaigns/${id}`)).data;

export const createCampaign = async (body) =>
  (await api.post('/campaigns', body)).data;

export const updateCampaign = async (id, body) =>
  (await api.put(`/campaigns/${id}`, body)).data;

export const regenerateCampaignQuestions = async (id) =>
  (await api.post(`/campaigns/${id}/regenerate-questions`)).data;

export const addCampaignCandidates = async (id, candidateIds) =>
  (await api.post(`/campaigns/${id}/candidates`, { candidate_ids: candidateIds })).data;

export const removeCampaignCandidate = async (id, candidateId) =>
  (await api.delete(`/campaigns/${id}/candidates/${candidateId}`)).data;

export const overrideCampaignCandidateQuestions = async (id, candidateId, questions) =>
  (await api.put(`/campaigns/${id}/candidates/${candidateId}/questions`, { questions })).data;

export const launchCampaign = async (id) =>
  (await api.post(`/campaigns/${id}/launch`)).data;

export const deleteCampaign = async (id) =>
  (await api.delete(`/campaigns/${id}`)).data;

// Parse an uploaded resume file into structured candidate data (multipart).
export const parseResumeFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('store_original', 'true');
  const response = await api.post('/candidates/parse-resume-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Organization CRUD APIs - FIXED: /organization/* → /organizations/*
export const getOrganizationsList = async () => {
  const response = await api.get('/organizations/get');
  return response.data;
};

export const addNewOrganization = async (orgData) => {
  const response = await api.post('/organizations/add', orgData);
  return response.data;
};

export const updateExistingOrganization = async (orgId, orgData) => {
  const response = await api.put(`/organizations/update/${orgId}`, orgData);
  return response.data;
};

export const getOrganizationDetails = async (orgId) => {
  const response = await api.get(`/organizations/findone/${orgId}`);
  return response.data;
};

export const deleteOrganizationById = async (orgId) => {
  const response = await api.delete(`/organizations/delete/${orgId}`);
  return response.data;
};

// Requirement CRUD APIs - FIXED: /requirement/* → /requirements/*
export const getRequirementsList = async () => {
  const response = await api.get('/requirements/get');
  return response.data;
};

export const addNewRequirement = async (reqData) => {
  const response = await api.post('/requirements/add', reqData);
  return response.data;
};

export const updateExistingRequirement = async (reqId, reqData) => {
  const response = await api.put(`/requirements/update/${reqId}`, reqData);
  return response.data;
};

export const getRequirementDetails = async (reqId) => {
  const response = await api.get(`/requirements/findone/${reqId}`);
  return response.data;
};

export const deleteRequirementById = async (reqId) => {
  const response = await api.delete(`/requirements/delete/${reqId}`);
  return response.data;
};

// ===== Client Management APIs =====
export const getClientsList = async () => {
  const response = await api.get('/clients/list');
  return response.data;
};

export const createClient = async (data) => {
  const response = await api.post('/clients/create', data);
  return response.data;
};

export const toggleClientStatus = async (userId) => {
  const response = await api.put(`/clients/${userId}/toggle-status`);
  return response.data;
};

export const resetClientPassword = async (userId, data) => {
  const response = await api.post(`/clients/${userId}/reset-password`, data);
  return response.data;
};

export const updateClient = async (userId, data) => {
  const response = await api.put(`/clients/${userId}/update`, data);
  return response.data;
};

export const updateClientQuota = async (orgId, data) => {
  const response = await api.put(`/clients/quota/${orgId}`, data);
  return response.data;
};

// Self-service password change
export const changeMyPassword = async (data) => {
  const response = await api.post('/users/change-password', data);
  return response.data;
};

// Organization image upload
export const uploadOrganizationImage = async (formData) => {
  const response = await api.post('/upload/organization-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// ===== Postprocessing / Report APIs =====
const POSTPROCESSING_API_BASE = process.env.REACT_APP_POSTPROCESSING_API_BASE;

const postprocessingApi = axios.create({
  baseURL: POSTPROCESSING_API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

postprocessingApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const getCompletedSessions = async () => {
  const response = await postprocessingApi.get('/reports/sessions/completed');
  return response.data;
};

export const generateReport = async (sessionId) => {
  const response = await postprocessingApi.post(`/reports/generate/${sessionId}`, {}, { timeout: 300000 });
  return response.data;
};

export const viewReport = async (sessionId) => {
  const response = await postprocessingApi.get(`/reports/view/${sessionId}`);
  return response.data;
};

export const listReports = async () => {
  const response = await postprocessingApi.get('/reports/list');
  return response.data;
};

export const downloadReportPdf = async (sessionId) => {
  const response = await postprocessingApi.get(`/reports/download/${sessionId}`, {
    responseType: 'blob',
    timeout: 60000,
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `report_${sessionId}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getRecordingDownloadUrl = async (sessionId) => {
  const response = await postprocessingApi.get(`/reports/recording-url/${sessionId}`);
  return response.data;
};

export const getCameraRecordingDownloadUrl = async (sessionId) => {
  const response = await postprocessingApi.get(`/reports/camera-recording-url/${sessionId}`);
  return response.data;
};

export default api;




// ── Cherry-picked from prepration-main for Coupons.jsx ─────────────────

export const getCoupons = async () => (await api.get('/coupons')).data;

export const toggleCoupon = async (code, active) => (await api.patch(`/coupons/${code}`, { active })).data;

// Candidate CRUD APIs - FIXED: /candidate/* → /candidates/*

export const createCoupon = async (body) => (await api.post('/coupons', body)).data;

// helper for cherry-picked download functions
const _downloadBlob = async (path, filename, timeout = 60000) => {
  const response = await api.get(path, { responseType: 'blob', timeout });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ─── Cherry-picked from prepration-main merge (2026-07-16) ────────────
// Callers: Campaigns.jsx (resendCampaignInvitation), ReportView.jsx (both
// download helpers). The rest of Downloads' api.js was NOT adopted — it
// deletes 229 lines of Local API methods that other pages still need.

export const resendCampaignInvitation = async (id, candidateId) =>
  (await api.post(`/campaigns/${id}/candidates/${candidateId}/resend`)).data;

export const downloadCameraRecording = async (sessionId) => {
  await _downloadBlob(`/reports/camera-recording/${sessionId}`, `camera_recording_${sessionId}.webm`);
};

export const downloadRecording = async (sessionId) => {
  await _downloadBlob(`/reports/recording/${sessionId}`, `recording_${sessionId}.webm`);
};
